import ActionModal from "@/pages/extension/modal/ActionModal";
import type { AcceptButtonDetails } from "@/pages/extension/modal/types";
import { TextField } from "@elaniintc/goes-design-system";
import { useCallback } from "react";
import { useForm, Controller } from "react-hook-form";

interface Props {
  isLoading?: boolean;
  isOpen: boolean;
  onClose: () => void;
  acceptButtonDetails: AcceptButtonDetails;
  onApprove: (daysGranted: string) => Promise<void>;
  maxDaysAllowed?: number;
}

const ApproveInputModal = ({
  isLoading,
  isOpen,
  onClose,
  acceptButtonDetails,
  maxDaysAllowed,
  onApprove,
}: Props) => {
  type FormValues = { daysGranted: string };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: { daysGranted: "" },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const onSubmit = (values: FormValues) => {
    onApprove(values.daysGranted);
    reset();
    onClose();
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={handleClose}
      onAccpet={handleSubmit(onSubmit)}
      title="Aprobación de plazo"
      description="Aprueba el plazo que se añadirá a este paso."
      acceptButtonDetails={{
        ...acceptButtonDetails,
        // disable when form is invalid or external loading flag is set
        disabled: !!(!isValid || isLoading),
        isLoading: !!isLoading,
      }}
    >
      <div className="pb-8">
        <Controller
          name="daysGranted"
          control={control}
          rules={{
            required: "Ingresa la cantidad de días",
            pattern: { value: /^\d+$/, message: "Solo números" },
            validate: (v: string) => {
              const n = parseInt(v || "0", 10);
              if (n <= 0) return "Debe ser mayor que 0";
              if (maxDaysAllowed && n > maxDaysAllowed)
                return `Solo se permiten ${maxDaysAllowed} días de plazo`;
              return true;
            },
          }}
          render={({ field }) => (
            <>
              <TextField
                required
                label="Tiempo de plazo en días"
                value={field.value}
                error={errors?.daysGranted?.message as string}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  const numericValue = value.replace(/\D/g, "");
                  field.onChange(numericValue);
                }}
                placeholder="Escribe el tiempo de aplazamiento en días"
                disabled={false}
              />
            </>
          )}
        />
      </div>
    </ActionModal>
  );
};

export default ApproveInputModal;
